import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Tool } from '../../types';
import { tools } from '../../data/tools';
import { useSearch } from '../../hooks/useSearch';
import styles from '../../styles/modules/SearchSuggestions.module.css';

interface SearchSuggestionsProps {
  searchQuery: string;
  onSuggestionSelect: (tool: Tool) => void;
  onClose: () => void;
}

const SearchSuggestions = ({ searchQuery, onSuggestionSelect, onClose }: SearchSuggestionsProps) => {
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { addToRecent } = useSearch();

  const getSuggestions = (): Tool[] => {
    if (!searchQuery.trim()) return [];

    const query = searchQuery.toLowerCase();
    const suggestions = tools.filter(tool => 
      tool.name.toLowerCase().includes(query) ||
      tool.description.toLowerCase().includes(query) ||
      tool.category.toLowerCase().includes(query) ||
      tool.tags.some(tag => tag.toLowerCase().includes(query))
    );

    // Sort by relevance: exact name matches first, then name starts with, then description matches
    return suggestions.sort((a, b) => {
      const aName = a.name.toLowerCase();
      const bName = b.name.toLowerCase();
      
      if (aName === query) return -1;
      if (bName === query) return 1;
      if (aName.startsWith(query) && !bName.startsWith(query)) return -1;
      if (bName.startsWith(query) && !aName.startsWith(query)) return 1;
      
      return 0;
    }).slice(0, 8); // Limit to 8 suggestions
  };

  const suggestions = getSuggestions();

  const handleSuggestionClick = useCallback((tool: Tool) => {
    addToRecent(tool);
    navigate(tool.path);
    onSuggestionSelect(tool);
    onClose();
  }, [addToRecent, navigate, onSuggestionSelect, onClose]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < suggestions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : suggestions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        }
        break;
      case 'Escape':
        onClose();
        break;
    }
  }, [suggestions, highlightedIndex, onClose, handleSuggestionClick]);

  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchQuery]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  if (suggestions.length === 0 && searchQuery.trim()) {
    return (
      <div className={styles.searchSuggestions} ref={suggestionsRef}>
        <div className={styles.noSuggestions}>
          No tools found for "{searchQuery}"
        </div>
      </div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  return (
    <div className={styles.searchSuggestions} ref={suggestionsRef}>
      {suggestions.map((tool, index) => (
        <div
          key={tool.id}
          className={`${styles.suggestionItem} ${
            index === highlightedIndex ? styles.highlighted : ''
          }`}
          onClick={() => handleSuggestionClick(tool)}
          onMouseEnter={() => setHighlightedIndex(index)}
        >
          <div className={styles.suggestionIcon}>{tool.icon}</div>
          <div className={styles.suggestionContent}>
            <div className={styles.suggestionName}>{tool.name}</div>
            <div className={styles.suggestionDescription}>{tool.description}</div>
          </div>
          <div className={styles.suggestionCategory}>{tool.category}</div>
        </div>
      ))}
    </div>
  );
};

export default SearchSuggestions;